import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Loader2, RefreshCw, X } from "lucide-react";
import { communityApi } from "./api";
import type { CommunityAggregateResponse, CommunityInterest } from "./types";
import { FiltersBar } from "../applications/shared/FiltersBar";
import {
  ApplicationsTable,
  type Column,
} from "../applications/shared/ApplicationsTable";
import { Section, Field, Chips } from "../applications/shared/InfoBlock";
import {
  formatApplicantName,
  formatDate,
  formatDateTime,
} from "../applications/shared/format";
import { LoadingBlock, ErrorBlock } from "../dashboard/shared/StateBlocks";
import { useDebounce } from "@/lib/utils/useDebounce";

const SOURCE_OPTIONS = [
  { value: "logged", label: "Sólo logueados" },
  { value: "anon", label: "Sólo anónimos" },
];

const FREQUENCY_LABELS: Record<string, string> = {
  weekly: "Semanal",
  monthly: "Mensual",
  quarterly: "Trimestral",
  rarely: "Rara vez",
  never: "Nunca",
};

const PAGE_SIZE = 30;

export function CommunityScreen() {
  const [search, setSearch] = useState("");
  const [source, setSource] = useState("");
  const [page, setPage] = useState(1);
  const [selectedId, setSelectedId] = useState<number | null>(null);

  const debouncedSearch = useDebounce(search, 300);

  useEffect(() => setPage(1), [debouncedSearch, source]);

  const filters = useMemo(
    () => ({
      q: debouncedSearch || undefined,
      onlyLogged: source === "logged",
      onlyAnon: source === "anon",
      page,
      pageSize: PAGE_SIZE,
    }),
    [debouncedSearch, source, page],
  );

  const aggregateQuery = useQuery({
    queryKey: ["communications", "community", "aggregate"],
    queryFn: () => communityApi.aggregate(),
  });

  const listQuery = useQuery({
    queryKey: ["communications", "community", filters],
    queryFn: () => communityApi.list(filters),
    placeholderData: (prev) => prev,
  });

  const detailQuery = useQuery({
    queryKey: ["communications", "community", "detail", selectedId],
    queryFn: () => communityApi.detail(selectedId!),
    enabled: selectedId !== null,
  });

  return (
    <div className="flex flex-col gap-4 animate-fade-in">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="nx-eyebrow mb-1">Comunicaciones · Comunidad</p>
          <h1 className="nx-h1">Quiz de comunidad</h1>
          <p className="text-sm text-fg-muted mt-1">
            Respuestas al quiz de <code>/comunidad</code>. Captura preferencias de
            usuarios logueados y visitantes anónimos.
          </p>
        </div>
        <button
          onClick={() => {
            aggregateQuery.refetch();
            listQuery.refetch();
          }}
          className="nx-btn-secondary"
          disabled={listQuery.isFetching || aggregateQuery.isFetching}
        >
          <RefreshCw
            size={14}
            className={
              listQuery.isFetching || aggregateQuery.isFetching ? "animate-spin" : ""
            }
          />
          Actualizar
        </button>
      </header>

      {aggregateQuery.data && <AggregatePanel data={aggregateQuery.data} />}

      <FiltersBar
        search={search}
        onSearchChange={setSearch}
        searchPlaceholder="Buscar por email o nombre…"
        status={source}
        onStatusChange={setSource}
        statusOptions={SOURCE_OPTIONS}
      />

      {listQuery.isLoading ? (
        <LoadingBlock label="Cargando respuestas…" />
      ) : listQuery.isError ? (
        <ErrorBlock onRetry={() => listQuery.refetch()} />
      ) : (
        <ApplicationsTable<CommunityInterest>
          rows={listQuery.data?.interests ?? []}
          columns={columns}
          page={listQuery.data?.page ?? 1}
          pageSize={listQuery.data?.pageSize ?? PAGE_SIZE}
          total={listQuery.data?.total ?? 0}
          onPageChange={setPage}
          onRowClick={(r) => setSelectedId(r.id)}
          selectedId={selectedId}
        />
      )}

      {selectedId !== null && (
        <InterestDrawer
          loading={detailQuery.isLoading}
          data={detailQuery.data ?? null}
          onClose={() => setSelectedId(null)}
        />
      )}
    </div>
  );
}

function AggregatePanel({ data }: { data: CommunityAggregateResponse }) {
  return (
    <section className="grid grid-cols-1 lg:grid-cols-3 gap-4">
      <div className="nx-card p-4">
        <h2 className="nx-h2 mb-3">Totales</h2>
        <div className="grid grid-cols-2 gap-y-2 gap-x-3">
          <Stat label="Total respuestas" value={data.total} />
          <Stat label="Últimos 7 días" value={data.last7Days} />
          <Stat label="Logueados" value={data.logged} />
          <Stat label="Anónimos" value={data.anon} />
          <Stat label="Con email" value={data.withEmail} />
          <Stat label="Quiere acceso anticipado" value={data.wantsEarlyAccess} />
        </div>
      </div>

      <div className="nx-card p-4">
        <h2 className="nx-h2 mb-3">Top géneros musicales</h2>
        <DistributionList items={data.musicGenres.slice(0, 8)} />
      </div>

      <div className="nx-card p-4">
        <h2 className="nx-h2 mb-3">Top ciudades preferidas</h2>
        <DistributionList items={data.preferredCities.slice(0, 8)} />
      </div>

      <div className="nx-card p-4">
        <h2 className="nx-h2 mb-3">Formatos de contenido</h2>
        <DistributionList items={data.contentFormats} />
      </div>

      <div className="nx-card p-4 lg:col-span-2">
        <h2 className="nx-h2 mb-3">Frecuencia de asistencia</h2>
        <DistributionList
          items={data.attendanceFrequency.map((i) => ({
            value: FREQUENCY_LABELS[i.value] ?? i.value,
            count: i.count,
          }))}
        />
      </div>
    </section>
  );
}

function Stat({ label, value }: { label: string; value: number }) {
  return (
    <div className="flex items-baseline justify-between">
      <span className="text-2xs text-fg-subtle">{label}</span>
      <span className="text-sm font-semibold text-fg tabular-nums">
        {value.toLocaleString("es-CL")}
      </span>
    </div>
  );
}

function DistributionList({ items }: { items: Array<{ value: string; count: number }> }) {
  if (items.length === 0) {
    return <div className="text-2xs text-fg-subtle">Sin datos.</div>;
  }
  const max = Math.max(...items.map((i) => i.count), 1);
  return (
    <div className="flex flex-col gap-2">
      {items.map((it) => {
        const widthPct = Math.max(4, Math.round((it.count / max) * 100));
        return (
          <div key={it.value} className="flex items-center gap-2">
            <span className="text-xs text-fg w-1/2 truncate">{it.value}</span>
            <div className="flex-1 bg-bg-subtle rounded h-3 relative overflow-hidden">
              <div
                className="h-full bg-accent transition-all duration-300"
                style={{ width: `${widthPct}%` }}
              />
            </div>
            <span className="text-2xs text-fg-subtle tabular-nums w-8 text-right">
              {it.count}
            </span>
          </div>
        );
      })}
    </div>
  );
}

const columns: Column<CommunityInterest>[] = [
  {
    key: "id",
    header: "ID",
    cell: (r) => <span className="text-fg-subtle tabular-nums">#{r.id}</span>,
    width: "min",
  },
  {
    key: "respondent",
    header: "Respondente",
    cell: (r) => (
      <div>
        <div className="font-medium text-fg">
          {r.user ? formatApplicantName(r.user) : r.displayName || "Anónimo"}
        </div>
        <div className="text-2xs text-fg-subtle break-all">
          {r.user?.email ?? r.email ?? "—"}
        </div>
      </div>
    ),
  },
  {
    key: "source",
    header: "Origen",
    cell: (r) => (
      <span className={r.userId ? "nx-badge-info" : "nx-badge-neutral"}>
        {r.userId ? "Logueado" : "Anónimo"}
      </span>
    ),
    width: "min",
  },
  {
    key: "genres",
    header: "Géneros",
    cell: (r) => (
      <div className="text-2xs text-fg-muted line-clamp-1 max-w-[200px]">
        {(r.answers.musicGenres ?? []).slice(0, 3).join(", ") || "—"}
      </div>
    ),
  },
  {
    key: "frequency",
    header: "Frecuencia",
    cell: (r) => (
      <span className="text-fg-muted text-xs">
        {r.answers.attendanceFrequency
          ? FREQUENCY_LABELS[r.answers.attendanceFrequency] ?? r.answers.attendanceFrequency
          : "—"}
      </span>
    ),
    width: "min",
  },
  {
    key: "earlyAccess",
    header: "Early access",
    cell: (r) =>
      r.answers.wantsEarlyAccess ? (
        <span className="nx-badge-success">Sí</span>
      ) : (
        <span className="text-fg-subtle text-xs">No</span>
      ),
    width: "min",
  },
  {
    key: "createdAt",
    header: "Recibido",
    cell: (r) => <span className="text-fg-subtle text-xs">{formatDate(r.createdAt)}</span>,
    width: "min",
    align: "right",
  },
];

function InterestDrawer({
  loading,
  data,
  onClose,
}: {
  loading: boolean;
  data: CommunityInterest | null;
  onClose: () => void;
}) {
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onClose]);

  return (
    <>
      <div className="fixed inset-0 bg-black/40 z-40 animate-fade-in" onClick={onClose} aria-hidden />
      <aside
        className="fixed top-0 right-0 bottom-0 z-50 w-full sm:w-[520px] bg-bg-card border-l border-border shadow-xl flex flex-col animate-slide-in-right"
        role="dialog"
        aria-modal="true"
      >
        <header className="flex items-start justify-between gap-3 px-5 py-4 border-b border-border">
          <div className="min-w-0">
            <h2 className="nx-h2 truncate">
              {data
                ? data.user
                  ? formatApplicantName(data.user)
                  : data.displayName || "Respuesta anónima"
                : "Respuesta"}
            </h2>
            {data && (
              <p className="text-2xs text-fg-subtle mt-0.5">
                #{data.id} · {formatDateTime(data.createdAt)}
              </p>
            )}
          </div>
          <button onClick={onClose} className="nx-btn-ghost p-1.5" aria-label="Cerrar">
            <X size={16} />
          </button>
        </header>

        <div className="flex-1 overflow-y-auto px-5 py-4">
          {loading || !data ? (
            <div className="flex items-center justify-center py-12 text-fg-subtle">
              <Loader2 size={16} className="animate-spin mr-2" />
              Cargando…
            </div>
          ) : (
            <div className="flex flex-col gap-5">
              <Section title="Respondente">
                <Field
                  label="Origen"
                  value={data.userId ? "Usuario logueado" : "Visitante anónimo"}
                />
                <Field label="User ID" value={data.userId ?? "—"} />
                <Field label="Nombre" value={data.displayName ?? formatApplicantName(data.user)} wide />
                <Field
                  label="Email"
                  value={
                    (data.user?.email || data.email) ? (
                      <a className="text-accent hover:underline" href={`mailto:${data.user?.email || data.email}`}>
                        {data.user?.email || data.email}
                      </a>
                    ) : (
                      "—"
                    )
                  }
                  wide
                />
              </Section>

              <Section title="Respuestas del quiz">
                <Field
                  label="Formatos de contenido"
                  value={<Chips items={data.answers.contentFormats ?? []} />}
                  wide
                />
                <Field
                  label="Géneros musicales"
                  value={<Chips items={data.answers.musicGenres ?? []} />}
                  wide
                />
                <Field
                  label="Ciudades preferidas"
                  value={<Chips items={data.answers.preferredCities ?? []} />}
                  wide
                />
                <Field
                  label="Frecuencia"
                  value={
                    data.answers.attendanceFrequency
                      ? FREQUENCY_LABELS[data.answers.attendanceFrequency] ??
                        data.answers.attendanceFrequency
                      : "—"
                  }
                />
                <Field
                  label="Acceso anticipado"
                  value={data.answers.wantsEarlyAccess ? "Sí" : "No"}
                />
              </Section>

              <Section title="Tracking">
                <Field label="IP" value={data.ipAddress ?? "—"} />
                <Field
                  label="Convertido"
                  value={data.convertedAt ? formatDateTime(data.convertedAt) : "—"}
                />
                <Field
                  label="User agent"
                  value={<span className="text-2xs break-all">{data.userAgent ?? "—"}</span>}
                  wide
                />
              </Section>
            </div>
          )}
        </div>
      </aside>
    </>
  );
}
